import Vue from 'vue';
import Router from 'vue-router';

import UserService from '@/services/UserService';
import LocationService from '@/services/LocationService';
import StoreLayout from '../layouts/StoreLayout.vue';
import InformationLayout from '../layouts/InformationLayout.vue';

Vue.use(Router);

function authGuard(to, from, next) {
  if (UserService.isVerified()) {
    const { query } = to;

    if (query.next) {
      window.location.href = query.next;
    } else {
      next('/account');
    }
  } else {
    next();
  }
}

// eslint-disable-next-line import/prefer-default-export
export function createRouter() {
  const router = new Router({
    mode: process.env.STOREFRONT_SERVER ? 'abstract' : 'history',
    routes: [
      {
        name: 'home',
        path: '/',
        component: () => import(/* webpackChunkName: "HomePage" */ '../pages/Home/HomePage.vue'),
        meta: {
          fullTitle: 'Modern, Mid Century & Scandinavian Furniture | Article',
          fullDescription:
            'Great style is easy. Article offers stylish modern, mid century, and scandinavian furniture from world renowned designers at accessible prices. Shop now.',
          newsletter: true,
        },
      },
      {
        name: 'homePreview',
        path: '/home/:id/:slug',
        component: () => import(/* webpackChunkName: "HomePage" */ '../pages/Home/HomePage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'product',
        path: '/product/:id/:slug',
        component: () => import(/* webpackChunkName: "ProductPage" */ '../pages/Product/ProductPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'pla',
        path: '/pla/:id/:slug',
        props: true,
        component: () => import(/* webpackChunkName: "PLAPage" */ '../pages/Product/PLAPage.vue'),
        beforeEnter(to, from, next) {
          if (to.query.forceCurrencyId) {
            LocationService.setIsLocationManual(false);
            delete to.query.forceCurrencyId;
            next(to);
          } else {
            next();
          }
        },
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'browse',
        path: '/browse/:id/:slug',
        component: () => {
          if (process.env.STOREFRONT) {
            return import(/* webpackChunkName: "BrowseNAPage" */ '../pages/Browse/BrowseNAPage.vue');
          } else {
            return import(/* webpackChunkName: "BrowsePage" */ '../pages/Browse/BrowsePage.vue');
          }
        },
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'room',
        path: '/room/:id/:slug',
        component: () => import(/* webpackChunkName: "RoomPage" */ '../pages/Room/RoomPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'search',
        path: '/search',
        component: () => import(/* webpackChunkName: "SearchPage" */ '../pages/Search/SearchPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'searchStatic',
        path: '/search/:slug.html',
        component: () => import(/* webpackChunkName: "SearchPage" */ '../pages/Search/SearchPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'landing',
        path: '/l/:slug',
        component: () => {
          if (process.env.STOREFRONT) {
            return import(/* webpackChunkName: "LandingNAPage" */ '../pages/Landing/LandingNAPage.vue')
          } else {
            return import(/* webpackChunkName: "LandingPage" */ '../pages/Landing/LandingPage.vue')
          }
        },
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'collection',
        path: '/collection/:id/:slug',
        component: () => {
          if (process.env.STOREFRONT) {
            return import(/* webpackChunkName: "CollectionNAPage" */ '../pages/Collection/CollectionNAPage.vue')
          } else {
            return import(/* webpackChunkName: "CollectionPage" */ '../pages/Collection/CollectionPage.vue')
          }
        },
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'sale',
        path: '/sale',
        component: () => import(/* webpackChunkName: "SalePage" */ '../pages/Sale/SalePage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'clearance',
        path: '/clearance',
        component: () => import(/* webpackChunkName: "ClearancePage" */ '../pages/Clearance/ClearancePage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'financing',
        path: '/financing',
        component: () => import(/* webpackChunkName: "FinancingPage" */ '../pages/Financing/FinancingPage.vue')
      },
      {
        name: 'currentPromotion',
        path: '/promotion',
        component: () => {
          if (process.env.STOREFRONT) {
            return import(/* webpackChunkName: "PromotionPage" */ '../pages/Promotion/PromotionNAPage.vue');
          }

          return import(/* webpackChunkName: "PromotionPage" */ '../pages/Promotion/PromotionPage.vue');
        },
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'promotion',
        path: '/promotion/:id/:slug',
        component: () => {
          if (process.env.STOREFRONT) {
            return import(/* webpackChunkName: "PromotionPage" */ '../pages/Promotion/PromotionNAPage.vue');
          }

          return import(/* webpackChunkName: "PromotionPage" */ '../pages/Promotion/PromotionPage.vue');
        },
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'tag',
        path: '/tag/:id/:slug',
        component: () => import(/* webpackChunkName: "TagPage" */ '../pages/Tag/TagPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'shop',
        path: '/shop',
        component: () => import(/* webpackChunkName: "ShopPage" */ '../pages/Shop/ShopPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'shopBrowse',
        path: '/shop/:slug',
        component: () => {
          if (process.env.STOREFRONT) {
            return import(/* webpackChunkName: "ShopBrowsePage" */ '../pages/Shop/ShopBrowsePage.vue')
          } else {
            return import(/* webpackChunkName: "BrowsePage" */ '../pages/Browse/BrowsePage.vue')
          }
        },
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'cart',
        path: '/cart',
        component: () => import(/* webpackChunkName: "CartPage" */ '../pages/Cart/CartPage.vue'),
        meta: {
          title: 'Checkout',
          description: 'Shop article.com for high quality furniture at incredible prices for your Dining, Living and Bedroom.',
        },
      },
      {
        name: 'cartAbandonment',
        path: '/cart/:sessionId',
        component: () => import(/* webpackChunkName: "CartAbandonmentPage" */ '../pages/Cart/CartAbandonmentPage.vue'),
        meta: {
          title: 'Checkout',
          description: 'Shop article.com for high quality furniture at incredible prices for your Dining, Living and Bedroom.',
        },
      },
      {
        name: 'cartQuoteAbandonment',
        path: '/cart/:quote/:sessionId',
        component: () => import(/* webpackChunkName: "CartAbandonmentPage" */ '../pages/Cart/CartAbandonmentPage.vue'),
        meta: {
          title: 'Checkout',
          description: 'Shop article.com for high quality furniture at incredible prices for your Dining, Living and Bedroom.',
        },
      },
      {
        name: 'affirmRedirect',
        path: '/api/checkout/affirmredirect/:paymentId',
        component: () => import(/* webpackChunkName: "AffirmRedirectPage" */ '../pages/Cart/AffirmRedirectPage.vue'),
        meta: {
          title: 'Redirecting to Affirm',
          description: 'Shop article.com for high quality furniture at incredible prices for your Dining, Living and Bedroom.',
        },
      },
      {
        name: 'paybrightRedirect',
        path: '/api/checkout/paybrightredirect/:paymentId',
        component: () => import(/* webpackChunkName: "PayBrightRedirectPage" */ '../pages/Cart/PayBrightRedirectPage.vue'),
        meta: {
          title: 'Redirecting to PayBright',
          description: 'Shop article.com for high quality furniture at incredible prices for your Dining, Living and Bedroom.',
        },
      },
      {
        name: 'resolveRedirect',
        path: '/api/checkout/resolvepayredirect/:paymentId',
        component: () => import(/* webpackChunkName: "ResolveRedirectPage" */ '../pages/Cart/ResolveRedirectPage.vue'),
        meta: {
          title: 'Redirecting to Resolve',
          description: 'Shop article.com for high quality furniture at incredible prices for your Dining, Living and Bedroom.',
        },
      },
      {
        name: 'lookbooks',
        path: '/lookbooks',
        component: () => import(/* webpackChunkName: "LookbooksPage" */ '../pages/Lookbooks/LookbooksPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'lookbook',
        path: '/lookbook/:id/:slug',
        component: () => import(/* webpackChunkName: "LookbookPage" */ '../pages/Lookbooks/LookbookPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'login',
        path: '/login',
        component: () => import(/* webpackChunkName: "LoginPage" */ '../pages/Auth/LoginPage.vue'),
        meta: {
          fullTitle: 'Account Login | Article',
          fullDescription:
            'Sign-in to your Article account to review your current and past orders, store credits, product reviews, and any active support requests. Log-in now.',
        },
        beforeEnter(to, from, next) {
          authGuard(to, from, next);
        },
      },
      {
        name: 'loginIncomplete',
        path: '/login/incomplete',
        component: () => import(/* webpackChunkName: "LoginIncompletePage" */ '../pages/Auth/LoginIncompletePage.vue'),
        meta: { title: 'Complete Account Set-Up' },
        beforeEnter(to, from, next) {
          authGuard(to, from, next);
        },
        props: true,
      },
      {
        name: 'forgot',
        path: '/forgot',
        component: () => import(/* webpackChunkName: "ForgotPage" */ '../pages/Auth/ForgotPage.vue'),
        meta: { title: 'Forgot Your Password?' },
      },
      {
        name: 'forgotSuccess',
        path: '/forgot/success',
        component: () => import(/* webpackChunkName: "ForgotSentPage" */ '../pages/Auth/ForgotSentPage.vue'),
        meta: { title: 'Forgot Password Instructions' },
      },
      {
        name: 'signup',
        path: '/signup',
        component: () => import(/* webpackChunkName: "RegisterPage" */ '../pages/Auth/RegisterPage.vue'),
        meta: { title: 'Sign Up' },
        beforeEnter(to, from, next) {
          authGuard(to, from, next);
        },
      },
      {
        name: 'completeRequired',
        path: '/complete/required',
        component: () => import(/* webpackChunkName: "CompleteRequiredPage" */ '../pages/Auth/CompleteRequiredPage.vue'),
        meta: { title: 'Complete Your Account' },
      },
      {
        name: 'socialOnly',
        path: '/complete/socialOnly',
        component: () => import(/* webpackChunkName: "SocialOnlyPage" */ '../pages/Auth/SocialOnlyPage.vue'),
        meta: { title: "You don't have a password" },
      },
      {
        name: 'verifyRequired',
        path: '/verify/required',
        component: () => import(/* webpackChunkName: "VerifyRequiredPage" */ '../pages/Auth/VerifyRequiredPage.vue'),
        meta: { title: 'Verify Your Account' },
      },
      {
        name: 'verifySuccess',
        path: '/verify/success',
        component: () => import(/* webpackChunkName: "VerifySuccessPage" */ '../pages/Auth/VerifySuccessPage.vue'),
        meta: { title: 'Account Verified' },
        beforeEnter(to, from, next) {
          if (to.query.__i) {
            UserService.setUser({
              sessionId: to.query.__i,
            });
          } else {
            UserService.setUser(null);
          }
          if (to.query.__t) {
            UserService.setCustomerToken(
                to.query.__t
            );
          } else {
            UserService.setCustomerToken(null);
          }
          next();
        },
      },
      {
        name: 'verifyError',
        path: '/verify/error',
        component: () => import(/* webpackChunkName: "VerifyErrorPage" */ '../pages/Auth/VerifyErrorPage.vue'),
        meta: { title: 'Verification Error' },
      },
      {
        name: 'resetRequired',
        path: '/reset/required',
        component: () => import(/* webpackChunkName: "ResetRequiredPage" */ '../pages/Auth/ResetRequiredPage.vue'),
        meta: { title: 'Update Your Password' },
      },
      {
        name: 'reset',
        path: '/reset/:cypher/:code',
        component: () => import(/* webpackChunkName: "ResetPasswordPage" */ '../pages/Auth/ResetPasswordPage.vue'),
        meta: { title: 'Update Your Password' },
      },
      {
        name: 'confirmation',
        path: '/checkout/confirm(/.*)?/:cypher',
        component: () => import(/* webpackChunkName: "ConfirmationPage" */ '../pages/Confirm/ConfirmationPage.vue'),
        meta: { title: 'Order Confirmed' },
      },
      {
        path: '/account',
        alias: 'menu',
        component: () => import(/* webpackChunkName: "AccountPage" */ '../components/account/AccountPage.vue'),
        beforeEnter(to, from, next) {
          if (!process.env.STOREFRONT_SERVER && !UserService.isVerified()) {
            next({
              name: 'login',
              query: {
                next: to.fullPath,
              },
              replace: true,
            });
          } else {
            next();
          }
        },
        children: [
          {
            name: 'myaccount',
            path: 'myaccount',
            redirect: {
              name: 'menu',
            },
          },
          {
            name: 'menu',
            path: '',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountNavigation" */ '../components/account/AccountNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountOrders" */ '../components/account/AccountOrders.vue'),
            },
          },
          {
            name: 'orders',
            path: 'orders',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountNavigation" */ '../components/account/AccountNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountOrders" */ '../components/account/AccountOrders.vue'),
            },
          },
          {
            path: 'personal',
            name: 'personal',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountNavigation" */ '../components/account/AccountNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountPersonal" */ '../components/account/AccountPersonal.vue'),
            },
          },
          {
            path: 'storeCredits',
            name: 'storeCredits',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountNavigation" */ '../components/account/AccountNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountCredits" */ '../components/account/AccountCredits.vue'),
            },
          },
          {
            path: 'changepassword',
            name: 'changepassword',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountNavigation" */ '../components/account/AccountNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountPassword" */ '../components/account/AccountPassword.vue'),
            },
          },
          {
            path: 'communication',
            name: 'communication',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountNavigation" */ '../components/account/AccountNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountCommunication" */ '../components/account/AccountCommunication.vue'),
            },
          },
          {
            path: 'reviews',
            name: 'accountReviews',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountNavigation" */ '../components/account/AccountNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountReviews" */ '../components/account/AccountReviews.vue'),
            },
          },
          {
            path: 'reviews/:productId',
            name: 'accountProductReview',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountReviewNavigation" */ '../components/account/AccountReviewNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountProductReview" */ '../components/account/AccountProductReview.vue'),
            }
          },
          {
            path: 'supportRequests',
            name: 'supportRequests',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountNavigation" */ '../components/account/AccountNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountSupport" */ '../components/account/AccountSupport.vue'),
            },
          },
          {
            path: 'supportRequest/:claimId',
            name: 'supportRequest',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountSupportNavigation" */ '../components/account/AccountSupportNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountSupportRequest" */ '../components/account/AccountSupportRequest.vue'),
            },
            props: {
              content: true,
            },
          },
          {
            path: 'supportRequest',
            name: 'supportRequestFallback',
            beforeEnter: (to, from, next) => {
              if (to.query && to.query.claimId) {
                next({
                  name: 'supportRequest',
                  params: {
                    claimId: to.query.claimId,
                  },
                  replace: true,
                });
                return;
              }

              next('/404');
            },
          },
          {
            path: 'orders/:orderId/track',
            name: 'track',
            components: {
              navigation: () => import(/* webpackChunkName: "AccountTrackNavigation" */ '../components/account/AccountTrackNavigation.vue'),
              content: () => import(/* webpackChunkName: "AccountTrack" */ '../components/account/AccountTrack.vue'),
            },
          },
        ],
      },
      {
        path: '/favorites',
        name: 'favorites',
        component: () => import(/* webpackChunkName: "FavoritePage" */ '../pages/Favorite/FavoritePage.vue'),
        meta: {
          title: 'Favorites',
        },
      },
      {
        path: '/favorites/:uuid',
        name: 'favoritesshare',
        component: () => import(/* webpackChunkName: "FavoritePage" */ '../pages/Favorite/FavoritePage.vue'),
        meta: {
          title: 'Favorites',
        },
      },
      {
        path: '/delivery',
        name: 'delivery',
        component: () => import(/* webpackChunkName: "DeliveryPage" */ '../pages/Static/DeliveryPage.vue'),
        meta: {
          acceptableQueries: [],
          fullTitle: 'Furniture Shipping & Delivery Services | Article',
          fullDescription:
            "We take delivery as seriously as we take design. Article's shipping is affordable, fast, and communicative. Find a breakdown of our shipping options here.",
          newsletter: true,
        },
      },
      {
        path: '/delivery/:slug',
        name: 'deliveryPreview',
        component: () => import(/* webpackChunkName: "DeliveryPage" */ '../pages/Static/DeliveryPage.vue'),
        meta: {
          acceptableQueries: [],
          title: 'Shipping Information',
          newsletter: true,
        },
      },
      {
        path: '/returns',
        name: 'returns',
        component: () => import(/* webpackChunkName: "ReturnsPage" */ '../pages/Static/ReturnsPage.vue'),
        meta: {
          fullTitle: 'Furniture Warranty, Returns & Exchanges | Article',
          fullDescription:
            'We want your space to feel perfect. That’s why we offer a 30-day return policy on all Article orders. Learn more about returns and exchanges.',
          newsletter: true,
        },
      },
      {
        path: '/returns/:slug',
        name: 'returnsPreview',
        component: () => import(/* webpackChunkName: "ReturnsPage" */ '../pages/Static/ReturnsPage.vue'),
        meta: {
          title: 'Returns & Exchanges',
          newsletter: true,
        },
      },
      {
        path: '/aboutus',
        name: 'aboutus',
        component: () => import(/* webpackChunkName: "AboutusPage" */ '../pages/Static/AboutusPage.vue'),
        meta: {
          fullTitle: 'About Us | Article',
          fullDescription:
            'Article is the easiest way to create a beautiful modern space. Find out more about the Article story and discover our core values here.',
          newsletter: true,
        },
      },
      {
        path: '/order/:token/verify',
        name: 'orderVerify',
        component: () => import(/* webpackChunkName: "OrderVerifyPage" */ '../pages/Order/OrderVerifyPage.vue'),
        meta: {
          layout: InformationLayout,
          title: 'Order Verification',
        },
      },
      {
        path: '/reviews/edit',
        component: () => import(/* webpackChunkName: "EditReviewsLayout" */ '../pages/EditReviews/EditReviewsLayout.vue'),
        children: [
          {
            path: '',
            name: 'editReviews',
            component: () => import(/* webpackChunkName: "EditReviewsPage" */ '../pages/EditReviews/EditReviewsPage.vue'),
            meta: {
              title: 'Your Reviews'
            }
          },
          {
            path: 'order',
            name: 'editOrderReviews',
            component: () => import(/* webpackChunkName: "EditOrderReviewsPage" */ '../pages/EditReviews/EditOrderReviewsPage.vue'),
            meta: {
              title: 'Review Your Experience'
            }
          },
          {
            path: 'product',
            name: 'editProductReviews',
            component: () => import(/* webpackChunkName: "EditReviewsProductPage" */ '../pages/EditReviews/EditProductReviewsPage.vue'),
            meta: {
              title: 'Review Your Product'
            }
          }
        ]
      },
      {
        // Deprecated - This can be removed after rolling out new review workflow
        path: '/order/:token/reviews',
        alias: '/order/:token/review',
        name: 'emailReview',
        component: () => import(/* webpackChunkName: "EmailReviewPage" */ '../pages/Order/EmailReviewPage.vue'),
        meta: {
          layout: InformationLayout,
          title: 'Your Article Experience.',
        },
      },
      {
        // Deprecated - This can be removed after rolling out new review workflow
        path: '/order/reviews/thanks',
        name: 'emailReviewThanks',
        component: () => import(/* webpackChunkName: "EmailReviewThanksPage" */ '../pages/Order/EmailReviewThanksPage.vue'),
        meta: {
          title: 'Thank You',
        },
      },
      {
        // Deprecated - This can be removed after rolling out new review workflow
        path: '/order/reviews/follow-up',
        name: 'emailReviewFollowUp',
        component: () => import(/* webpackChunkName: "EmailReviewFollowUpPage" */ '../pages/Order/EmailReviewFollowUpPage.vue'),
        meta: {
          title: 'Thank You',
        },
      },
      {
        // Deprecated - This can be removed after rolling out new review workflow
        path: '/product/reviews',
        name: 'review',
        component: () => import(/* webpackChunkName: "ReviewPage" */ '../pages/Order/ReviewPage.vue'),
        meta: {
          title: 'Review',
        },
      },
      {
        // Deprecated - This can be removed after rolling out new review workflow
        path: '/order/:token/reviewProduct',
        name: 'createProductReview',
        component: () => import(/* webpackChunkName: "CreateProductReview" */ '../components/order/CreateProductReview.vue'),
        meta: {
          layout: InformationLayout,
          title: 'Rate Your Article Product.',
        },
      },
      {
        path: '/giftcards/:token/verify',
        name: 'giftCardVerify',
        component: () => import(/* webpackChunkName: "GiftCardVerifyPage" */ '../pages/Giftcards/GiftCardVerifyPage.vue'),
        meta: {
          layout: InformationLayout,
          title: 'Gift Card Verification',
        },
      },
      {
        path: '/giftcards',
        name: 'giftcards',
        component: () => import(/* webpackChunkName: "GiftCardPage" */ '../pages/Giftcards/GiftCardPage.vue'),
        children: [
          {
            path: 'buy',
            name: 'giftCardBuy',
            component: () => import(/* webpackChunkName: "GiftCardBuy" */ '../pages/Giftcards/GiftCardBuy.vue'),
          },
          {
            path: 'confirm/:token',
            name: 'giftCardConfirmation',
            component: () => import(/* webpackChunkName: "GiftCardConfirmation" */ '../pages/Giftcards/GiftCardConfirmation.vue'),
            meta: {
              title: 'Gift Card Purchase',
            },
          },
          {
            path: 'terms',
            name: 'giftCardTerms',
            component: () => import(/* webpackChunkName: "GiftCardTerms" */ '../pages/Giftcards/GiftCardTerms.vue'),
            meta: {
              layout: InformationLayout,
              title: 'Gift Card',
            },
          },
          {
            path: 'activate/:token',
            name: 'giftCardActivate',
            component: () => import(/* webpackChunkName: "GiftCardTerms" */ '../pages/Giftcards/GiftCardActivate.vue'),
            meta: {
              layout: InformationLayout,
              title: 'Gift Card',
            },
          },
        ],
      },
      {
        path: '/press',
        name: 'press',
        component: () => import(/* webpackChunkName: "PressPage" */ '../pages/Static/PressPage.vue'),
        meta: {
          fullTitle: 'In the Press | Article',
          fullDescription:
            "We're proud of our press and media content. Read about Article in the news and visit our media kit for brand assets and press resources.",
          newsletter: true,
        },
      },
      {
        path: '/contact',
        name: 'contact',
        component: () => import(/* webpackChunkName: "ContactPage" */ '../pages/Contact/ContactPage.vue'),
      },
      {
        path: '/contact/message',
        name: 'message',
        component: () => import(/* webpackChunkName: "ContactMessagePage" */ '../pages/Contact/ContactMessagePage.vue'),
      },
      {
        path: '/contact/data',
        name: 'data',
        component: () => import(/* webpackChunkName: "MyDataPage" */ '../pages/Contact/MyDataPage.vue'),
        meta: {
          title: 'My Data',
        },
      },
      {
        path: '/sitemap',
        name: 'sitemap',
        component: () => import(/* webpackChunkName: "SitemapPage" */ '../pages/Sitemap/SitemapPage.vue'),
      },
      {
        path: '/careers',
        name: 'careers',
        component: () => import(/* webpackChunkName: "CareersPage" */ '../pages/Static/CareersPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        path: '/tou',
        name: 'termsOfUse',
        component: () => import(/* webpackChunkName: "TermsOfUsePage" */ '../pages/Static/TermsOfUsePage.vue'),
      },
      {
        path: '/tou/:slug',
        name: 'termsOfUsePreview',
        component: () => import(/* webpackChunkName: "TermsOfUsePage" */ '../pages/Static/TermsOfUsePage.vue'),
      },
      {
        path: '/reviews',
        name: 'customerReviews',
        component: () => import(/* webpackChunkName: "ReviewsPage" */ '../pages/Static/ReviewsPage.vue'),
        meta: {
          fullTitle: 'Furniture Reviews | Article',
          fullDescription:
            "Don't just take our word for it. Read more than 33,500 reviews from happy and stylish Article customers. See for yourself: browse now.",
          newsletter: true,
        },
      },
      {
        path: '/privacy',
        name: 'privacyPolicy',
        component: () => import(/* webpackChunkName: "PrivacyPage" */ '../pages/Static/PrivacyPage.vue'),
      },
      {
        path: '/privacy/:slug',
        name: 'privacyPreview',
        component: () => import(/* webpackChunkName: "ReturnsPage" */ '../pages/Static/PrivacyPage.vue'),
      },
      {
        path: '/covid-19',
        name: 'COVID19Information',
        component: () => import(/* webpackChunkName: "COVID19InformationPage" */ '../pages/Static/COVID19Information.vue'),
      },
      {
        path: '/covid-19/:slug',
        name: 'COVID19InformationPreview',
        component: () => import(/* webpackChunkName: "COVID19InformationPage" */ '../pages/Static/COVID19Information.vue'),
      },
      {
        path: '/faq',
        name: 'faq',
        component: () => import(/* webpackChunkName: "FaqPage" */ '../pages/Static/FaqPage.vue'),
        meta: {
          acceptableQueries: ['q'],
          newsletter: true,
        },
      },
      {
        path: '/value',
        name: 'value',
        component: () => import(/* webpackChunkName: "ValuePage" */ '../pages/Static/ValuePage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        path: '/accessibility',
        name: 'accessibility',
        component: () => import(/* webpackChunkName: "AccessibilityPage" */ '../pages/Static/AccessibilityPage.vue'),
      },
      {
        path: '/trade',
        name: 'tradeSignUp',
        component: () => import(/* webpackChunkName: "TradePage" */ '../pages/Trade/TradePage.vue'),
        meta: {
          title: 'Article Trade Program',
        },
      },
      {
        path: '/business',
        name: 'businessSignUp',
        component: () => import(/* webpackChunkName: "BusinessPage" */ '../pages/Trade/BusinessPage.vue'),
        meta: {
          title: 'Article Business Program',
        },
      },
      {
        path: '/furniture-bundles',
        name: 'sets',
        component: () => import(/* webpackChunkName: "SetsPage" */ '../pages/Sets/SetsPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        path: '/furniture-bundles/:id/:slug',
        name: 'set',
        component: () => import(/* webpackChunkName: "SetPage" */ '../pages/Sets/SetPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        path: '/ourarticle',
        name: 'ugcDisplay',
        component: () => import(/* webpackChunkName: "UGCDisplayPage" */ '../pages/UGC/UGCDisplayPage.vue'),
        meta: {
          title: 'Article Social Club',
          newsletter: true,
        },
      },

      {
        path: '/sofa-finder',
        name: 'productFinder',
        component: () => import(/* webpackChunkName: "ProductFinderLayout" */ '../pages/ProductFinder/ProductFinderLayout.vue'),
        meta: {
          title: 'Sofa Finder',
        },
      },

      {
        path: '/help-center',
        component: () => import(/* webpackChunkName: "HelpCenterLayout" */ '../pages/HelpCenter/HelpCenterLayout.vue'),
        children: [
          {
            path: '',
            name: 'helpCenter',
            component: () => import(/* webpackChunkName: "HelpCenterPage" */ '../pages/HelpCenter/HelpCenterPage.vue'),
          },
          {
            path: 'search',
            name: 'helpCenterSearch',
            component: () => import(/* webpackChunkName: "HelpCenterSearchPage" */ '../pages/HelpCenter/HelpCenterSearchPage.vue'),
          },
          {
            path: ':page',
            name: 'helpCenterTopic',
            component: () => import(/* webpackChunkName: "HelpCenterTopic" */ '../pages/HelpCenter/HelpCenterTopic.vue'),
          },
          {
            path: ':page/:tag',
            name: 'helpCenterTag',
            component: () => import(/* webpackChunkName: "HelpCenterPage" */ '../pages/HelpCenter/HelpCenterTopic.vue'),
          },
        ],
      },

      
      {
        path: '/:discount',
        name: 'homeUrlDiscount',
        component: () => import(/* webpackChunkName: "HomePage" */ '../pages/Home/HomePage.vue'),
        beforeEnter(to, from, next) {
          if (window.INITIAL_JSON && window.INITIAL_JSON.discount) {
            next();
            return;
          }
          next({ name: 'notFound', params: [to.path], replace: true });
        },
        meta: {
          newsletter: true,
        },
      },


      
      {
        path: '*',
        alias: '/500',
        name: 'serverError',
        component: () => import(/* webpackChunkName: "ServerErrorPage" */ '../pages/Errors/ServerErrorPage.vue'),
        meta: {
          title: 'Internal Server Error',
        },
      },
      {
        path: '*',
        alias: '/404',
        name: 'notFound',
        component: () => import(/* webpackChunkName: "NotFoundPage" */ '../pages/Errors/NotFoundPage.vue'),
      },
    ],
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      if (to.name !== from.name) {
        return { x: 0, y: 0 };
      }
    },
  });

  router.beforeEach((to, from, next) => {
    const errorCode = window.INITIAL_JSON?.common?.errorCode;

    if (to.name !== 'serverError' && (to.path === '/500' || errorCode === 500)) {
      next({ name: 'serverError', params: [to.path], replace: true });
      return;
    }

    if (to.name !== 'notFound' && to.path !== '/500' && (to.path === '/404' || errorCode === 404)) {
      next({ name: 'notFound', params: [to.path], replace: true });
      return;
    }

    if (to.meta && (to.meta.fullTitle || to.meta.title)) {
      const title = 'Article';
      // const description = 'Shop article.com for high quality furniture at incredible prices for your Dining, Living and Bedroom.';
      if (to.meta.fullTitle) {
        document.title = to.meta.fullTitle;
      } else if (to.meta.title) {
        document.title = `${to.meta.title} | ${title}`;
      } else {
        document.title = title;
      }

      if (to.meta.fullDescription) {
        document.querySelector("meta[name='description']")?.setAttribute('content', to.meta.fullDescription);
      }
    }

    if (to.meta && !to.meta.layout) {
      to.meta.layout = StoreLayout;
    }

    next();
  });

  return router;
}
